import {createSlice} from '@reduxjs/toolkit'
import ResourceService from '../../api/resource'
import Statistics from '../../api/statistics'
import {doNothing} from '../../utils/placeholder'
import text from '../text.json'
import {toast} from 'react-toastify'
import {routes} from '../../routes'
import TextHelper from '../../utils/text'
import roundValue from '../../utils/math'
import FormHelper from '../../utils/forms'
import statusCodes from '../../utils/http'
import NavigationHelper from '../../utils/navigation'
import {formatInTimeZone} from 'date-fns-tz'
import {DateFormats} from '../../utils/date'
import {buildMetricBasedRulesetsPayload, buildMetricOptions, mapMetricRulesetsResponse} from '../../utils/rulesetMetrics'
import {getAllLimitUsages} from '../auth'

const EXCLUDED_TYPES = ['free_gb', 'n_cpus', 'uptime']
const UTC = 'UTC'
const UNIFIED_TYPE = 'unified'
const CPU_TOTAL = 'cpu-total'

const getMetricTypeValues = (serverResource) => {
    return Object.values(serverResource?.grouped_metric_types || {})
        .flatMap(metric => metric)
        ?.filter(value => value !== null && value !== undefined)
}

const getMetricTypeStateObjects = (serverResource) => {
    const obj = {}
    const metricValues = getMetricTypeValues(serverResource)
    for (let i = 0; i < metricValues.length; i++) {
        obj[metricValues[i]] = {
            commonMetricsInfo: {},
            absoluteAdditionalMetrics: {},
            categories: [],
            metricsData: [],
            groupedData: {},
            additionalMetricsData: []
        }
    }
    return obj
}

export const serverMonitoringSlice = createSlice({
    name: 'server-monitoring',
    initialState:  {
        serverResource: {},
        isResourceFetching: false,
        isButtonLoading: false,
        isMetricsDataRetrieving: {},
        metricSources: [],
        rulesets: [],
        persistedRulesetCount: 0,
        isRulesetsFetching: false,
        isRulesetsDirty: false,
        isRulesetsSaving: false,
        rulesetMetricKeys: [],
        isRulesetMetricKeysFetching: false,
        serverResourceId: '',
        ...getMetricTypeStateObjects({})
    },
    reducers: {
        setServerResource: (state, {payload}) => {
            state.serverResource = payload.data
            if (payload.isConfigured) {
                return
            }
            const metricTypeObjects = getMetricTypeStateObjects(payload.data)
            Object.assign(state, metricTypeObjects)
        },
        setRulesetMetricKeys: (state, {payload}) => {
            state.rulesetMetricKeys = payload
        },
        setIsRulesetMetricKeysFetching: (state, {payload}) => {
            state.isRulesetMetricKeysFetching = payload
        },
        setServerResourceId: (state, {payload}) => {
            state.serverResourceId = payload
        },
        setIsResourceFetching: (state, {payload}) => {
            state.isResourceFetching = payload
        },
        setIsButtonLoading: (state, {payload}) => {
            state.isButtonLoading = payload
        },
        setIsMetricsDataRetrieving: (state, {payload}) => {
            const {type, isLoading} = payload
            state.isMetricsDataRetrieving = {
                ...state.isMetricsDataRetrieving,
                [type]: isLoading
            }
        },
        setCommonMetricsInfo: (state, {payload}) => {
            const {type, data} = payload
            state[type].commonMetricsInfo = data
        },
        setMetricsData: (state, {payload}) => {
            const {type, data} = payload
            state[type].metricsData = data
        },
        setMetricSources: (state, {payload}) => {
            state.metricSources = payload
        },
        setAbsoluteAdditionalMetrics: (state, {payload}) => {
            const {type, data} = payload
            const filteredWidgetTypes = state[type].metricsData?.[state[type].metricsData?.length - 1]?.extra?.filter(data => EXCLUDED_TYPES?.includes(data?.type))
            const additionalMetrics = {}

            if (state[type].commonMetricsInfo?.unit === UNIFIED_TYPE && filteredWidgetTypes?.length > 0) {
                filteredWidgetTypes.map(currentWidget => {
                    const {value, type: widgetType} = currentWidget
                    additionalMetrics[widgetType] = {
                        value: Number.isFinite(value) ? roundValue(value, 2) : value,
                        unit: currentWidget.unit
                    }
                })
                state[type].absoluteAdditionalMetrics = additionalMetrics
                return
            }

            data && Object.keys(data).map(key => {
                additionalMetrics[key] = {value: roundValue(data[key]), unit: state[type].commonMetricsInfo.unit}
            })
            state[type].absoluteAdditionalMetrics = additionalMetrics
        },
        setCategories: (state, {payload}) => {
            const {type, data} = payload
            state[type].categories = data
        },
        setGroupedData: (state, {payload}) => {
            const {type, data} = payload
            state[type].groupedData = data
        },
        setAdditionalMetricsData: (state, {payload}) => {
            const {type, data} = payload
            state[type].additionalMetricsData = data
        },
        setRulesets: (state, {payload}) => {
            const {ruleset, index} = payload

            state.isRulesetsDirty = true

            if (index > -1) {
                state.rulesets[index] = ruleset
                return
            }

            state.rulesets = [
                ...state.rulesets,
                ruleset
            ]
        },
        resetRulesets: (state) => {
            state.rulesets = []
            state.isRulesetsDirty = false
            state.persistedRulesetCount = 0
        },
        setAllRulesets: (state, {payload}) => {
            state.rulesets = payload
            state.isRulesetsDirty = false
            state.persistedRulesetCount = payload?.length ?? 0
        },
        setIsRulesetsFetching: (state, {payload}) => {
            state.isRulesetsFetching = payload
        },
        setIsRulesetsDirty: (state, {payload}) => {
            state.isRulesetsDirty = payload
            if (!payload) state.persistedRulesetCount = state.rulesets.length
        },
        setIsRulesetsSaving: (state, {payload}) => {
            state.isRulesetsSaving = payload
        },
        removeRuleset: (state, {payload}) => {
            state.rulesets = state.rulesets.filter((_, index) => index !== payload)
            state.isRulesetsDirty = true
        }
    }
})

export const {
    setServerResource,
    setRulesetMetricKeys,
    setIsRulesetMetricKeysFetching,
    setServerResourceId,
    setIsResourceFetching,
    setIsButtonLoading,
    setIsMetricsDataRetrieving,
    setCommonMetricsInfo,
    setAbsoluteAdditionalMetrics,
    setCategories,
    setMetricsData,
    setGroupedData,
    setAdditionalMetricsData,
    setMetricSources,
    setRulesets,
    resetRulesets,
    setAllRulesets,
    setIsRulesetsFetching,
    setIsRulesetsDirty,
    setIsRulesetsSaving,
    removeRuleset
} = serverMonitoringSlice.actions

export default serverMonitoringSlice.reducer

const ERROR_MSG = 'An error occured. Please try again later'
export const addServerResource = (payload) => {
    return async dispatch => {
        dispatch(setIsButtonLoading(true))
        try {
            const {data} = await ResourceService.addServerResource(payload)
            dispatch(setServerResourceId(data?.data?.server_resource?.id))
            dispatch(getAllLimitUsages())
            return {success: true}
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message || ERROR_MSG
            FormHelper.setFormElementError(fields, message)
            return {success: false}
        } finally {
            dispatch(setIsButtonLoading(false))
        }
    }
}

export const getServerResource = (serverMetrics, showLoader = false, id, navigate, isEdit = false, isConfigured = false) => {
    return async(dispatch) => {
        showLoader && dispatch(setIsResourceFetching(true))
        try {
            const {data} = await ResourceService.getServerResource(id)
            await dispatch(setServerResource({
                data: data?.data?.server_resource,
                isConfigured
            }))
            !isEdit && await dispatch(getServerResourceMetrics(serverMetrics, showLoader))
        } catch (e) {
            const res = e?.response
            if (statusCodes.SpecialErrors.includes(res?.status)) {
                navigate(NavigationHelper.getLocalizedPath(routes.not_found.paths))
            }
        }
        showLoader && dispatch(setIsResourceFetching(false))
    }
}

export const updateServerResource = (payload, isEdit = false, id = null, showMsg = true) => {
    return async(dispatch, getState) => {
        dispatch(setIsButtonLoading(true))
        try {
            const resourceId = id || getState().server_monitoring.serverResourceId
            const {data} = await ResourceService.updateServerResource(resourceId, payload)
            const successMessage = !id && !isEdit
                ? text.create_success_msg
                : (isEdit ? data?.message : text.update_server_resource_status_msg)

            showMsg && toast.success(successMessage)
            dispatch(getAllLimitUsages())
            return {success: true}
        } catch (e) {
            const res = e?.response?.data
            const fields = res?.data?.fields
            const message = res?.message || ERROR_MSG
            FormHelper.setFormElementError(fields, message)
            return {success: false}
        } finally {
            dispatch(setIsButtonLoading(false))
        }
    }
}

const fetchMetricsData = async(type, query, metricType, limit, serverMetrics, serverResourceId) => {
    const modifiedQuery = `${query ? `?type=${type}&${query}` : `?type=${type}`}${
        type === serverMetrics.processes_unified.value && !metricType && limit ? `&limit=${limit}` : ''
    }`
    const {data} = await Statistics.getServerResourceMetrics(serverResourceId, modifiedQuery)
    return data?.data || {}
}

const clearLoadingState = (dispatch, showLoader, type) => {
    showLoader && dispatch(setIsMetricsDataRetrieving({type, isLoading: false}))
}

export const getServerResourceMetrics = (serverMetrics, showLoader = false, query = null, metricType = null, limit = 1) => {
    return async(dispatch, getState) => {
        const serverResource = getState().server_monitoring.serverResource
        const metricValues = getMetricTypeValues(serverResource)

        if (showLoader) {
            const types = metricType ? [metricType] : metricValues
            types.forEach(type => dispatch(setIsMetricsDataRetrieving({type, isLoading: true})))
        }

        if (metricType) {
            try {
                const data = await fetchMetricsData(metricType, query, metricType, limit, serverMetrics, serverResource?.id)

                if (!data?.metrics) {
                    return {categories: [], groupedData: {}}
                }

                const categories = await dispatch(updateCategoriesAction(metricType, true, data.metrics))
                const updatedGroupedData = groupMetricsData(data.metrics, metricType, null, serverMetrics)

                return {categories, groupedData: updatedGroupedData}
            } catch {
                return {categories: [], groupedData: {}}
            } finally {
                clearLoadingState(dispatch, showLoader, metricType)
            }
        }

        await Promise.allSettled(
            metricValues.map(async(type) => {
                try {
                    const data = await fetchMetricsData(type, query, metricType, limit, serverMetrics, serverResource?.id)

                    if (!data?.common || !data?.metrics) {
                        await dispatch(setCommonMetricsInfo({type, data: null}))
                        await dispatch(setAbsoluteAdditionalMetrics({type, data: null}))
                        await dispatch(setCategories({type, data: []}))
                        await dispatch(setMetricsData({type, data: []}))
                        await dispatch(setGroupedData({type, data: {}}))
                        await dispatch(readyData(type, serverMetrics))
                        return
                    }

                    await dispatch(setCommonMetricsInfo({type, data: data.common}))
                    await dispatch(setMetricsData({type, data: data.metrics}))
                    await dispatch(readyData(type, serverMetrics))
                    await dispatch(setAbsoluteAdditionalMetrics({type, data: data.absolute_metric_additional}))

                    type === serverMetrics.network_usage_unified.value && await dispatch(setMetricSources(data?.metric_sources))
                } finally {
                    clearLoadingState(dispatch, showLoader, type)
                }
            })
        )
    }
}

const readyData = (type, serverMetrics) => {
    return async(dispatch) => {
        await dispatch(updateCategoriesAction(type))
        await dispatch(updateAdditionalMetricsAction(type))
        await dispatch(updateGroupedDataAction(type, serverMetrics))
    }
}

const updateCategoriesAction = (type, shouldReturnData = false, newMetricsData = null) => {
    return async(dispatch, getState) => {
        try {
            const state = getState().server_monitoring
            const {metricsData} = state[type]

            const timezone = state.serverResource?.timezone || UTC

            const data = (newMetricsData || metricsData).map(metric =>
                formatInTimeZone(
                    new Date(metric?.datetime),
                    timezone,
                    DateFormats.ISO_DATETIME
                )
            )

            if (shouldReturnData) {
                return data
            }

            dispatch(setCategories({type, data}))
        } catch {
            doNothing()
        }
    }
}

const groupMetricsData = (metricsData, type, additionalMetricsData = null, serverMetrics) => {
    const {charts} = Object.values(serverMetrics).find(metric => metric.value === type)
    const result = {}

    Object.entries(charts).forEach(([chartKey, chartMeta]) => {
        const {chart_key: chartType, grouping} = chartMeta

        metricsData.forEach(({value, extra = []}) => {
            if (!grouping) {
                handleDefaultGrouping(result, chartMeta, value)
                return
            }

            extra.forEach(extraItem => {
                if (!isValidExtra(extraItem, chartMeta, grouping, chartType, serverMetrics)) return

                const key = getGroupKey(grouping, serverMetrics)
                const keyValue = extraItem[key]

                result[chartKey] = result[chartKey] || []
                let group = result[chartKey].find(item => item[key] === keyValue)

                if (!group) {
                    group = createGroup(extraItem, key)
                    result[chartKey].push(group)
                }

                group.data.push(roundValue(extraItem.value))
            })
        })

        if (isEmptyActiveUsersChart(chartKey, result, serverMetrics)) {
            delete result[chartKey]
        }

        if (type === serverMetrics.cpu_total_usage.value) {
            result[chartKey] = result[chartKey].map(item =>
                ({
                    ...item.name === CPU_TOTAL
                        ? {...item, name: serverMetrics.cpu_total_usage.charts?.[chartKey].total_cpu_name}
                        : item
                })
            )
        }

        if (!grouping && additionalMetricsData?.length > 0) {
            result.default = result.default.concat(additionalMetricsData)
        }
    })

    return result
}

const handleDefaultGrouping = (result, chartMeta, value) => {
    result.default = result.default || [{data: [], unit: chartMeta.unit}]
    result.default[0].data.push(roundValue(value))
}

const isValidExtra = (extra, chartMeta, grouping, chartType, serverMetrics) => {
    if (EXCLUDED_TYPES.includes(extra.type) ||
    (grouping === serverMetrics.disk_usage_unified.charts.usage_gb.grouping && extra.type === 'total')) return false
    if (grouping === serverMetrics.cpu_total_usage.charts.usage.grouping && extra.type !== chartType) return false
    if ((grouping === serverMetrics.memory_usage.charts.total_used.grouping ||
        grouping !== serverMetrics.cpu_total_usage.charts.usage.grouping) && extra.unit !== chartMeta.unit) return false
    return true
}

const getGroupKey = (grouping, serverMetrics) => grouping === serverMetrics.memory_usage.charts.total_used.grouping ?
    serverMetrics.memory_usage.charts.total_used.grouping : 'name'

const createGroup = (extra, keyField) => ({
    [keyField]: extra[keyField],
    unit: extra.unit,
    data: []
})

const isEmptyActiveUsersChart = (chartKey, result, serverMetrics) => {
    const activeChartKey = serverMetrics.system_load_unified.charts.active_users.chart_key
    if (chartKey !== activeChartKey) return false

    return !result[chartKey]?.some(group => group.data?.some(value => value !== 0))
}

const updateGroupedDataAction = (type, serverMetrics) => {
    return async(dispatch, getState) => {
        try {
            const {metricsData, additionalMetricsData} = getState().server_monitoring[type]
            const groupedData = groupMetricsData(metricsData, type, additionalMetricsData, serverMetrics)
            dispatch(setGroupedData({type, data: groupedData}))
        } catch {
            doNothing()
        }
    }
}

const getAdditionalMetricsData = (metricsData, commonMetricsInfo) => {
    const additionalData = {}
    metricsData
        ?.filter((data) => data.metric_additional)
        ?.forEach((data) => {
            Object.keys(data.metric_additional).forEach((key) => {
                if (key !== 'avg') {
                    additionalData[key] = additionalData[key] || {name: TextHelper.capitalizeFirstChar(key), data: [], unit: commonMetricsInfo?.unit}
                    additionalData[key].data.push(roundValue(data.metric_additional[key]))
                }
            })
        })

    return Object.values(additionalData)
}

const updateAdditionalMetricsAction = (type) => {
    return async(dispatch, getState) => {
        try {
            const {metricsData, commonMetricsInfo} = getState().server_monitoring[type]
            const additionalMetricsData = getAdditionalMetricsData(metricsData, commonMetricsInfo)
            dispatch(setAdditionalMetricsData({type, data: additionalMetricsData}))
        } catch (e) {
            doNothing()
        }
    }
}

export const getRulesetMetricKeys = () => {
    return async dispatch => {
        dispatch(setIsRulesetMetricKeysFetching(true))
        try {
            const {data} = await ResourceService.getRulesetMetricKeys()
            const metricKeys = (data?.data?.ruleset_metrics || []).map(metric => ({
                key: metric.name,
                category: metric.server_metric_type,
                label: metric.name,
                is_setup_required: metric.is_setup_required
            }))

            dispatch(setRulesetMetricKeys(metricKeys))
            return {success: true}
        } catch (e) {
            const res = e?.response?.data
            const message = res?.message || ERROR_MSG
            toast.error(message)
            return {success: false}
        } finally {
            dispatch(setIsRulesetMetricKeysFetching(false))
        }
    }
}

export const getMetricBasedRulesets = (serverResourceId, {serverMetrics, conditionTypeOptions, valueTypeOptions} = {}) => {
    return async(dispatch, getState) => {
        if (!serverResourceId) return {success: false}

        dispatch(setIsRulesetsFetching(true))
        try {
            let {rulesetMetricKeys} = getState().server_monitoring
            if (!rulesetMetricKeys?.length) {
                await dispatch(getRulesetMetricKeys())
                rulesetMetricKeys = getState().server_monitoring.rulesetMetricKeys
            }

            const {data} = await ResourceService.getMetricRulesets(serverResourceId)

            const metricOptions = buildMetricOptions(rulesetMetricKeys, serverMetrics)
            const rulesets = mapMetricRulesetsResponse(data?.data?.metric_rulesets ?? [], {
                metricOptions,
                conditionTypeOptions,
                valueTypeOptions
            })

            dispatch(setAllRulesets(rulesets))
            return {success: true}
        } catch (e) {
            const res = e?.response?.data
            const message = res?.message || ERROR_MSG
            toast.error(message)
            return {success: false}
        } finally {
            dispatch(setIsRulesetsFetching(false))
        }
    }
}

const buildServerResourceUpdatePayload = (serverResource, rulesets) => ({
    service_id: serverResource?.service_id,
    name: serverResource?.name,
    chart_type_default: serverResource?.chart_type_default,
    description: serverResource?.description,
    timezone: serverResource?.timezone,
    os: serverResource?.os,
    metric_types_basic: serverResource?.grouped_metric_types?.basic,
    metric_types_advanced: serverResource?.grouped_metric_types?.advanced,
    metric_types_complex: serverResource?.grouped_metric_types?.complex,
    enabled: serverResource?.enabled,
    interval: serverResource?.settings?.interval,
    setup_complete: true,
    metric_based_rulesets: buildMetricBasedRulesetsPayload(rulesets)
})

export const persistMetricBasedRulesets = (serverResource) => {
    return async(dispatch, getState) => {
        if (!serverResource?.id) {
            return {success: false}
        }

        dispatch(setIsRulesetsSaving(true))
        try {
            const {rulesets} = getState().server_monitoring
            const result = await dispatch(updateServerResource(
                buildServerResourceUpdatePayload(serverResource, rulesets),
                false,
                serverResource.id
            ))

            if (result?.success) {
                dispatch(setIsRulesetsDirty(false))
            }

            return result
        } finally {
            dispatch(setIsRulesetsSaving(false))
        }
    }
}
